import React, { useContext, useMemo, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CSSTransition } from 'react-transition-group';
import { useTranslation } from 'react-i18next';
import useModalComponent from './useModalComponent';
import { ACTIONS, STATES } from './useModalTrigger';
import './Modal.scss';

const ModalContext = React.createContext();

const useModalContext = () => {
    const context = useContext(ModalContext);
    if (!context) {
        throw new Error(
            'Modal components are compound components. Must be used inside Modal.'
        );
    }
    return context;
};

// Optional close btn for modal, include with compound component <Modal.Close>
const Close = ({ ...props }) => {
    const { t } = useTranslation();
    const { onClose } = useModalContext();
    return (
        <div className="modal__close">
            <button
                className={classNames('modal__close-btn', props.className)}
                onClick={onClose}
                type="button"
                aria-label={t('close', { defaultValue: 'Close' })}>
                <svg
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                    focusable="false">
                    <path
                        d="M17.254.456a1.565 1.565 0 012.213.004c.613.616.613 1.61 0 2.226l-7.25 7.276 7.323 7.35c.613.615.613 1.61 0 2.226a1.565 1.565 0 01-2.213.004l-.004-.004L10 12.188l-7.323 7.35a1.566 1.566 0 01-2.217 0 1.577 1.577 0 010-2.226l7.322-7.35-7.25-7.276a1.577 1.577 0 010-2.226A1.565 1.565 0 012.747.456L2.75.46 10 7.736 17.25.46z"
                        fillRule="nonzero"
                    />
                </svg>
            </button>
        </div>
    );
};

Close.propTypes = {
    classNames: PropTypes.string.isRequired,
};

const Modal = ({ id, title, currentState, onClose, ...props }) => {
    const [modalPortalRef, setModalPortalRef] = useState({
        current: null,
    });

    const {
        modalsContainerRef,
        modalRef,
        titleId,
        handleEntered,
        handleExited,
        handleOverlayClick,
    } = useModalComponent(id, onClose);

    const context = useMemo(() => ({ onClose }), [onClose]);

    useEffect(() => {
        if (
            !modalsContainerRef &&
            document.querySelector(Modal.CONTAINER_SELECTOR)
        ) {
            const modalPortalRefs = {
                current: document.querySelector(Modal.CONTAINER_SELECTOR),
            };
            setModalPortalRef(modalPortalRefs);
        } else if (modalsContainerRef) {
            setModalPortalRef(modalsContainerRef);
        }
    }, []);

    const modal = (
        <ModalContext.Provider value={context}>
            <CSSTransition
                in={currentState === STATES.OPEN}
                classNames={{
                    appear: 'modal-root--appear',
                    appearActive: 'modal-root--appear-active',
                    appearDone: 'modal-root--appear-done',
                    enter: 'modal-root--enter',
                    enterActive: 'modal-root--enter-active',
                    enterDone: 'modal-root--enter-done',
                    exit: 'modal-root--exit',
                    exitActive: 'modal-root--exit-active',
                    exitDone: 'modal-root--exit-done',
                }}
                timeout={props.duration}
                mountOnEnter
                unmountOnExit
                onEntered={() => handleEntered()}
                onExited={() => handleExited()}>
                <div
                    ref={modalRef}
                    id={id}
                    className={classNames('modal-root', props.className)}>
                    <div
                        className={classNames('modal')}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby={titleId}>
                        <h1 id={titleId} className="visually-hidden">
                            {title}
                        </h1>
                        {props.children}
                    </div>
                    <div
                        className="modal__overlay"
                        onClick={() => handleOverlayClick()}
                        role="none"
                    />
                </div>
            </CSSTransition>
        </ModalContext.Provider>
    );

    // Render modal-root to modals container element
    return modalPortalRef?.current
        ? ReactDOM.createPortal(modal, modalPortalRef.current)
        : null;
};

// Use this static property to change the container selector
Modal.CONTAINER_SELECTOR = '.site-modals';
Modal.ACTIONS = ACTIONS;
Modal.STATES = STATES;
Modal.Close = Close;

Modal.propTypes = {
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    currentState: PropTypes.oneOf(Object.values(STATES)).isRequired,
    duration: PropTypes.number,
    className: PropTypes.string,
    children: PropTypes.node.isRequired,
    onClose: PropTypes.func.isRequired,
};

Modal.defaultProps = {
    duration: 250,
    className: null,
};

export default Modal;
